import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getPublicPrograms, PublicProgram } from "../../lib/publicApi";
import StarField from "../../components/StarField";

export default function Programs() {
  const [programs, setPrograms] = useState<PublicProgram[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getPublicPrograms()
      .then(setPrograms)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-obloha-indigo-950 to-obloha-indigo-900 py-24">
        <StarField density={0.00005} />
        <div className="pointer-events-none absolute left-1/4 top-1/2 h-96 w-96 rounded-full bg-purple-500/20 blur-3xl" />
        <div className="pointer-events-none absolute right-1/4 top-1/4 h-96 w-96 rounded-full bg-obloha-gold-500/15 blur-3xl" />

        <div className="relative mx-auto max-w-4xl px-6 text-center">
          <div className="mb-4 inline-block rounded-full border border-obloha-gold-400/30 bg-obloha-gold-500/10 px-4 py-1 text-xs font-semibold uppercase tracking-wider text-obloha-gold-400">
            Naše nabídka
          </div>
          <h1 className="text-5xl font-bold md:text-6xl">
            <span className="bg-gradient-to-b from-white to-obloha-indigo-200 bg-clip-text text-transparent">
              Programy pro školky
            </span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate-200">
            Každý program je pečlivě připravený pro konkrétní věkovou skupinu. Vyberte si jeden
            nebo jich zkombinujte víc — vesmír je velký.
          </p>
        </div>
      </section>

      {/* Programy */}
      <section className="relative overflow-hidden bg-obloha-indigo-950 py-16">
        <div className="mx-auto max-w-6xl px-6">
          {loading ? (
            <div className="text-center text-slate-400">Načítám…</div>
          ) : programs.length === 0 ? (
            <div className="rounded-2xl border border-white/10 bg-white/5 p-12 text-center text-slate-300">
              Programy se právě připravují.
            </div>
          ) : (
            <div className="grid gap-8 md:grid-cols-2">
              {programs.map((p) => (
                <ProgramCardLarge key={p.id} p={p} />
              ))}
            </div>
          )}

          <div className="mt-16 rounded-3xl border border-obloha-gold-400/30 bg-gradient-to-br from-obloha-gold-500/10 via-transparent to-purple-500/10 p-10 text-center backdrop-blur">
            <h3 className="text-2xl font-bold md:text-3xl">Nejste si jistí, co vybrat?</h3>
            <p className="mx-auto mt-3 max-w-2xl text-slate-300">
              Rádi vám poradíme podle věku a počtu dětí. Stačí nám napsat.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link
                to="/kontakt"
                className="rounded-full border border-white/30 px-6 py-2.5 font-medium hover:bg-white/10"
              >
                Napsat nám
              </Link>
              <Link
                to="/rezervace"
                className="rounded-full bg-obloha-gold-500 px-6 py-2.5 font-semibold text-obloha-indigo-950 hover:bg-obloha-gold-400"
              >
                Rezervovat
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function ProgramCardLarge({ p }: { p: PublicProgram }) {
  return (
    <Link
      to={`/programy/${p.id}`}
      className="group relative block overflow-hidden rounded-3xl border border-white/10 bg-white/5 backdrop-blur transition-all hover:-translate-y-1 hover:border-obloha-gold-400/50 hover:shadow-2xl hover:shadow-obloha-gold-500/20"
    >
      <div className="aspect-video w-full overflow-hidden bg-obloha-indigo-800">
        {p.coverImageUrl ? (
          <img
            src={p.coverImageUrl}
            alt={p.name}
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
        ) : (
          <div className="flex h-full items-center justify-center bg-gradient-to-br from-obloha-indigo-700 to-purple-800 text-8xl">
            ✨
          </div>
        )}
      </div>
      <div className="p-8">
        <h2 className="text-2xl font-bold transition-colors group-hover:text-obloha-gold-400 md:text-3xl">
          {p.name}
        </h2>
        {p.description && (
          <p className="mt-3 text-slate-300 line-clamp-3">{p.description}</p>
        )}

        <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
          <InfoPill icon="⏱" label="Délka">
            {p.durationMinutes} min
          </InfoPill>
          <InfoPill icon="👥" label="Kapacita">
            max {p.maxChildrenPerSlot} dětí
          </InfoPill>
          {p.ageGroupFrom != null && p.ageGroupTo != null && (
            <InfoPill icon="👶" label="Věk">
              {p.ageGroupFrom}–{p.ageGroupTo} let
            </InfoPill>
          )}
          <InfoPill icon="💰" label="Cena">
            od {p.minPriceCzk.toLocaleString("cs-CZ")} Kč
          </InfoPill>
        </div>

        <div className="mt-6 flex items-center text-sm font-semibold text-obloha-gold-400">
          Zobrazit detail
          <svg
            className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </div>
      </div>
    </Link>
  );
}

function InfoPill({
  icon,
  label,
  children,
}: {
  icon: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl bg-white/5 px-4 py-3">
      <div className="text-xs uppercase text-slate-400">
        <span className="mr-1">{icon}</span>
        {label}
      </div>
      <div className="mt-0.5 font-medium text-white">{children}</div>
    </div>
  );
}
